import { createSelector, createSlice, type Draft, type PayloadAction } from '@reduxjs/toolkit';
import omit from 'lodash/omit';
import { betaFeatures } from '@common/constants/betaFeatures';
import { useAppSelector } from '@common/store';
import { type StoreState } from '@common/types/store';

// NB! Every valid feature flag source must have this flag set
export const validityCheckFlag = 'FLAGS_ARE_VALID';

const supportedVariants = [
  validityCheckFlag,
  'AllowAccountDeactivation',
  'DebugLogging',
  'DesignHelpers',
  'DevicesListMap',
  'EnableTranslationLogger',
  'EnforceEula',
  'HideLoginTroubleSection',
  'MobileAppBanner',
  'SessionRecording',
  'StatisticsTabFullMenu',
  'SupportContactsInsteadOfFeedback',
  'Themes',
  'XmasPicture',
  'AnnotateAggregatedDataHistorySentinels',
  'LoginVideo',
  'BetaAccess',
  ...betaFeatures,
] as const;

export type Feature = typeof supportedVariants[number];

export type BetaFeatureKey = typeof betaFeatures[number];

// Flags that only PostHog may turn on; the fallback source never can
const postHogOnlyFlags: readonly string[] = ['BetaAccess', ...betaFeatures];

export const isBetaFeature = (key: string | null): key is BetaFeatureKey =>
  (betaFeatures as readonly string[]).includes(key ?? '');

type FeatureFlagValue = boolean | string;

export type Variants = Partial<Record<Feature, FeatureFlagValue>>;

type BooleanFlags = Record<Feature, boolean>;

const defaultReturnValue = APP_TYPE === 'mystiebel' ? false : DEBUG;

// Set default values for all mutable feature flags except for validityCheckFlag
const initialDefaults: BooleanFlags = {
  [validityCheckFlag]: true,
  AllowAccountDeactivation: defaultReturnValue,
  DebugLogging: defaultReturnValue,
  DesignHelpers: defaultReturnValue,
  DevicesListMap: defaultReturnValue,
  EnableTranslationLogger: defaultReturnValue,
  EnforceEula: defaultReturnValue,
  HideLoginTroubleSection: defaultReturnValue,
  MobileAppBanner: defaultReturnValue,
  SessionRecording: defaultReturnValue,
  StatisticsTabFullMenu: defaultReturnValue,
  SupportContactsInsteadOfFeedback: defaultReturnValue,
  Themes: defaultReturnValue,
  XmasPicture: defaultReturnValue,
  AnnotateAggregatedDataHistorySentinels: defaultReturnValue,
  LoginVideo: defaultReturnValue,
  BetaAccess: false,
  ...(Object.fromEntries(betaFeatures.map((feature) => [feature, false])) as Record<
    BetaFeatureKey,
    false
  >),
};

export type FeatureFlagsStoreState = {
  active: Variants;
  fallback: Variants;
  hasAuthoritativeActiveFlags: boolean;
};

const initialState: FeatureFlagsStoreState = {
  active: { ...initialDefaults },
  fallback: { ...initialDefaults },
  hasAuthoritativeActiveFlags: false,
};

// Change the fields within the original Variants object
const changeInPlace = (
  currentFlags: Draft<Variants>,
  newFlags: Variants,
  removeNonMatching?: boolean, // remove entries that are not present in newFlags
) => {
  Object.values(supportedVariants).forEach((feature) => {
    if (newFlags[feature]) {
      if (newFlags[feature] !== currentFlags[feature]) {
        currentFlags[feature] = newFlags[feature];
      }
    } else if (currentFlags[feature] && removeNonMatching) {
      delete currentFlags[feature];
    }
  }, {});
};

const slice = createSlice({
  initialState,
  name: 'featureFlags',
  reducers: {
    setFeatureFlags: (
      state: Draft<FeatureFlagsStoreState>,
      action: PayloadAction<{ featureFlags: Variants; flagsHaveAuthority?: boolean; }>,
    ) => {
      const { featureFlags, flagsHaveAuthority } = action.payload;

      // All valid sources for feature flags should have this flag present and set to true
      if (!featureFlags.FLAGS_ARE_VALID) {
        return;
      }

      if (flagsHaveAuthority) { // Coming from PostHog?
        changeInPlace(state.active, featureFlags, true);
        state.hasAuthoritativeActiveFlags = true;
      } else {
        const fallbackFlags = omit(featureFlags, postHogOnlyFlags);
        changeInPlace(state.fallback, fallbackFlags);
        // if we have nothing from PostHog yet, use the fallback flags also for active set
        if (!state.hasAuthoritativeActiveFlags) {
          changeInPlace(state.active, fallbackFlags);
        }
      }
    },
    clearFeatureFlags: (
      state: Draft<FeatureFlagsStoreState>,
    ) => {
      changeInPlace(state.active, state.fallback);
      postHogOnlyFlags.forEach((key) => {
        delete state.active[key as Feature];
      });
      state.hasAuthoritativeActiveFlags = false;
    },
  },
});

// Selector converts FeatureFlagValue => boolean
const selectActiveFeatureFlags = createSelector(
  (state: StoreState) => state.featureFlags.active,
  (active) =>
    Object.values(supportedVariants)
      .reduce<BooleanFlags>((accumulator, value) => {
        return {
          ...accumulator,
          [value]: !!active[value] && (!isBetaFeature(value) || !!active.BetaAccess),
        };
      }, {
        ...initialDefaults,
      }),
);

export const useAllFeatureFlags = () => {
  const { hasAuthoritativeActiveFlags } = useAppSelector(({ featureFlags }) => featureFlags);
  const featureFlags = useAppSelector(selectActiveFeatureFlags);
  return {
    hasAuthoritativeActiveFlags,
    featureFlags,
  };
};

export const useFeatureFlag = (feature: Feature) => {
  const featureFlags = useAppSelector(selectActiveFeatureFlags);
  return featureFlags[feature];
};

export const {
  setFeatureFlags,
  clearFeatureFlags,
} = slice.actions;

export default slice.reducer;
